import * as AlertDialog from '@radix-ui/react-alert-dialog';
import noop from 'lodash/noop';
import { twMerge } from 'tailwind-merge';

import { Button } from '../Button';

interface AlertProps {
  readonly open: boolean;
  readonly type: 'danger' | 'confirm';
  readonly title: string;
  readonly description: string | React.ReactElement;
  readonly loading?: boolean;
  readonly okButtonName?: string;
  readonly onConfirm?: () => void;
  readonly onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
}

const alertVariants = {
  danger: 'bg-red-700 text-white',
  confirm: 'bg-blue-400 text-white',
};

function Alert({ open, type, title, description, okButtonName = 'OK', loading, onConfirm = noop, onOpenChange }: AlertProps) {
  const buttonType = type === 'confirm' ? 'primary' : type;

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/40 transition-opacity" />
        <AlertDialog.Content
          className={twMerge(
            'fixed left-2/4 top-1/4 z-50 -mt-8  w-11/12 -translate-x-2/4 -translate-y-2/4 overflow-auto rounded-md bg-white font-serif shadow-md',
            'md:w-screen md:max-w-2xl',
            'focus:outline-none',
          )}
        >
          <AlertDialog.Title className={twMerge('m-0 p-4 font-medium', alertVariants[type])}>{title}</AlertDialog.Title>
          <AlertDialog.Description className="p-4 text-sm">{description}</AlertDialog.Description>
          <div className="flex items-center justify-end space-x-2 border-t border-neutral-200 p-3.5">
            <AlertDialog.Cancel asChild>
              <Button variant="secondary">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <Button loading={loading} variant={buttonType} onClick={onConfirm} data-testid="confirm-btn">
                {okButtonName}
              </Button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export { Alert };
